// Mirrors server/services/csv-headers; a parity test pins the two together.
// Imported statically, not via i18next: normalization runs inside a CSV parser.

import { ORDERED_CASE_TYPES } from '@app/constants/case-types';
import i18n from '@app/i18n';
// From locale-constants, not the barrel: suites that mock '@app/i18n' omit this.
import { SUPPORTED_LOCALES } from '@app/i18n/locale-constants';
import enCaseTypeValues from '@app/i18n/locales/en/caseTypeValues.json';
import enLabels from '@app/i18n/locales/en/csvHeaders.json';
import enExposureTypeValues from '@app/i18n/locales/en/exposureTypeValues.json';
import enLanguageValues from '@app/i18n/locales/en/languageValues.json';
import frCACaseTypeValues from '@app/i18n/locales/fr-CA/caseTypeValues.json';
import frCALabels from '@app/i18n/locales/fr-CA/csvHeaders.json';
import frCAExposureTypeValues from '@app/i18n/locales/fr-CA/exposureTypeValues.json';
import frCALanguageValues from '@app/i18n/locales/fr-CA/languageValues.json';
import type { ExposureType } from '@app/services/exposures';
import { CaseTypeEnum } from '@app/types/create-case';

export type CsvColumnKey = keyof typeof enLabels;

// These strings are the contract: downstream code and saved files key off them.
export const ENGLISH_LABELS: Record<CsvColumnKey, string> = enLabels;

export const CANONICAL_KEYS = Object.keys(enLabels) as CsvColumnKey[];

const BUNDLES: Record<string, Record<string, string>> = {
  en: enLabels,
  'fr-CA': frCALabels,
};

// Keyed off SUPPORTED_LOCALES so a missing bundle fails the parity test.
export const LABELS_BY_LOCALE: Record<
  string,
  Record<string, string>
> = Object.fromEntries(
  SUPPORTED_LOCALES.filter((locale) => BUNDLES[locale]).map((locale) => [
    locale,
    BUNDLES[locale],
  ]),
);

const DEFAULT_LOCALE = 'en';

// Without the BOM, Excel guesses the local codepage and mangles accents.
export const UTF8_BOM = '\uFEFF';

// Response.text() in doRequest strips the server's BOM, so downloads re-add it.
export const withBom = (csv: string): string =>
  `${UTF8_BOM}${csv.replace(/^\uFEFF/, '')}`;

// Column order of each generated file.
export const CSV_CATALOG = {
  MESSAGE_TEMPLATE_IMPORT: [
    'branding',
    'businessSegment',
    'lineOfBusiness',
    'lineOfBusinessName',
    'templateName',
    'message',
    'applicableCaseTypes',
    'exportId',
  ],
  MESSAGE_TEMPLATE_ORG_IMPORT: ['templateName', 'message'],
  BULK_USERS: [
    'uniqueId',
    'firstName',
    'lastName',
    'email',
    'title',
    'phoneNumber',
    'officeLine',
    'roles',
    'allowedCaseTypes',
    'exposureTypes',
    'groupMembers',
    'groupLeads',
    'language',
  ],
  CHANGE_UNIQUE_IDS: ['oldUniqueId', 'newUniqueId'],
  // The users export adds columns the upload template does not carry.
  BULK_USERS_EXPORT: [
    'uniqueId',
    'firstName',
    'lastName',
    'title',
    'lastLogin',
    'phoneNumber',
    'officeLine',
    'email',
    'roles',
    'allowedCaseTypes',
    'exposureTypes',
    'groupMembers',
    'groupLeads',
    'language',
    'active',
  ],
} satisfies Record<string, CsvColumnKey[]>;

// Folds away everything a hand-retyped or Excel-saved header can differ by.
export const comparisonKey = (label: string | undefined): string =>
  String(label ?? '')
    .replace(/\uFEFF/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

// English goes last so it wins any collision with a future locale's label.
const ALIASES = new Map<string, CsvColumnKey>();
[...Object.values(LABELS_BY_LOCALE), ENGLISH_LABELS].forEach((labels) => {
  Object.entries(labels).forEach(([canonicalKey, label]) => {
    if (!CANONICAL_KEYS.includes(canonicalKey as CsvColumnKey)) return;
    ALIASES.set(comparisonKey(label), canonicalKey as CsvColumnKey);
  });
});

export const normalizeHeader = (
  label: string | undefined,
): CsvColumnKey | null => ALIASES.get(comparisonKey(label)) ?? null;

export type LanguageCode = keyof typeof enLanguageValues;

// Machine contract, pinned to the server copy.
export const LANGUAGE_VALUES: Record<LanguageCode, string[]> = enLanguageValues;

export const LANGUAGE_BUNDLES: Record<string, Record<string, string[]>> = {
  en: enLanguageValues,
  'fr-CA': frCALanguageValues,
};

// Code aliases last so English wins any collision with a translation.
const LANGUAGE_ALIASES = new Map<string, LanguageCode>();
[...Object.values(LANGUAGE_BUNDLES), LANGUAGE_VALUES].forEach((values) => {
  Object.entries(values).forEach(([code, aliases]) => {
    aliases.forEach((alias) =>
      LANGUAGE_ALIASES.set(comparisonKey(alias), code as LanguageCode),
    );
  });
});

export type ParsedLanguage =
  | { ok: true; value: LanguageCode | null }
  | { ok: false };

/** Blank = leave alone; unusable = row error. */
export const parseLanguage = (cell: string | undefined): ParsedLanguage => {
  const compare = comparisonKey(cell);
  if (!compare) return { ok: true, value: null };
  const code = LANGUAGE_ALIASES.get(compare);
  return code ? { ok: true, value: code } : { ok: false };
};

export const ALL_CASE_TYPES_KEY = 'ALL';

export const CASE_TYPE_VALUES: Record<string, string[]> = enCaseTypeValues;

export const CASE_TYPE_BUNDLES: Record<string, Record<string, string[]>> = {
  en: enCaseTypeValues,
  'fr-CA': frCACaseTypeValues,
};

const CASE_TYPE_ALIASES = new Map<string, string>();
[...Object.values(CASE_TYPE_BUNDLES), CASE_TYPE_VALUES].forEach((values) => {
  Object.entries(values).forEach(([caseType, aliases]) => {
    aliases.forEach((alias) =>
      CASE_TYPE_ALIASES.set(comparisonKey(alias), caseType),
    );
  });
});

export type ParsedAllowedCaseTypes =
  | { ok: true; value: CaseTypeEnum[] | null }
  | { ok: false; reason: 'unknown'; invalid: string[] }
  | { ok: false; reason: 'service' };

export const parseAllowedCaseTypes = (
  cell: string | undefined,
  { serviceEnabled = false }: { serviceEnabled?: boolean } = {},
): ParsedAllowedCaseTypes => {
  const tokens = String(cell ?? '')
    .split(/[;,]/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
  if (tokens.length === 0) return { ok: true, value: null };

  const invalid = tokens.filter(
    (token) => !CASE_TYPE_ALIASES.has(comparisonKey(token)),
  );
  if (invalid.length > 0) return { ok: false, reason: 'unknown', invalid };

  const resolved = new Set(
    tokens.map((token) => CASE_TYPE_ALIASES.get(comparisonKey(token))),
  );
  const assignable = ORDERED_CASE_TYPES.filter(
    (caseType) => caseType !== CaseTypeEnum.service || serviceEnabled,
  );
  if (resolved.has(ALL_CASE_TYPES_KEY)) return { ok: true, value: assignable };

  if (resolved.has(CaseTypeEnum.service) && !serviceEnabled) {
    return { ok: false, reason: 'service' };
  }
  return {
    ok: true,
    value: ORDERED_CASE_TYPES.filter((caseType) => resolved.has(caseType)),
  };
};

export const NO_EXPOSURE_TYPES_KEY = 'NONE';

export const EXPOSURE_TYPE_BUNDLES: Record<string, Record<string, string[]>> = {
  en: enExposureTypeValues,
  'fr-CA': frCAExposureTypeValues,
};

const NO_EXPOSURE_TYPES_ALIASES = new Set(
  Object.values(EXPOSURE_TYPE_BUNDLES)
    .flatMap((values) => values[NO_EXPOSURE_TYPES_KEY])
    .map(comparisonKey),
);

type ExposureTypeAliases = Pick<ExposureType, 'id' | 'key' | 'name'>;

// Keyed by the list itself: validateCSV parses every row against the same array.
const idsByAliasCache = new WeakMap<
  ExposureTypeAliases[],
  Map<string, string>
>();

const idsByAliasFor = (
  exposureTypes: ExposureTypeAliases[],
): Map<string, string> => {
  const cached = idsByAliasCache.get(exposureTypes);
  if (cached) return cached;
  const idsByAlias = new Map<string, string>();
  exposureTypes.forEach(({ id, name, key }) => {
    idsByAlias.set(comparisonKey(name), id);
    if (key) idsByAlias.set(comparisonKey(key), id);
  });
  idsByAliasCache.set(exposureTypes, idsByAlias);
  return idsByAlias;
};

export type ParsedExposureTypes =
  | { ok: true; value: string[] | null }
  | { ok: false; reason: 'unknown'; invalid: string[] }
  | { ok: false; reason: 'none-combined' };

interface KeyedToken {
  token: string;
  key: string;
}

const isNone = ({ key }: KeyedToken): boolean =>
  NO_EXPOSURE_TYPES_ALIASES.has(key);

export const parseExposureTypes = (
  cell: string | undefined,
  exposureTypes: ExposureTypeAliases[],
): ParsedExposureTypes => {
  const tokens = String(cell ?? '')
    .split(/[;,]/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
  if (tokens.length === 0) return { ok: true, value: null };

  const idsByAlias = idsByAliasFor(exposureTypes);
  const keyedTokens = tokens.map((token) => ({
    token,
    key: comparisonKey(token),
  }));

  const invalid = keyedTokens
    .filter((keyed) => !isNone(keyed) && !idsByAlias.has(keyed.key))
    .map(({ token }) => token);
  if (invalid.length > 0) return { ok: false, reason: 'unknown', invalid };

  if (keyedTokens.some(isNone)) {
    return keyedTokens.every(isNone)
      ? { ok: true, value: [] }
      : { ok: false, reason: 'none-combined' };
  }

  const requested = new Set(keyedTokens.map(({ key }) => idsByAlias.get(key)));
  return {
    ok: true,
    value: exposureTypes.map(({ id }) => id).filter((id) => requested.has(id)),
  };
};

// Unknown columns are dropped; a repeated column keeps its last occurrence.
export const normalizeColumns = (
  columns: Array<string | undefined> = [],
): Partial<Record<CsvColumnKey, number>> => {
  const indices: Partial<Record<CsvColumnKey, number>> = {};
  columns.forEach((column, index) => {
    const canonicalKey = normalizeHeader(column);
    if (canonicalKey) {
      indices[canonicalKey] = index;
    }
  });
  return indices;
};

export const normalizeRow = (
  row: Record<string, string>,
): Partial<Record<CsvColumnKey, string>> => {
  const normalized: Partial<Record<CsvColumnKey, string>> = {};
  Object.entries(row).forEach(([label, value]) => {
    const canonicalKey = normalizeHeader(label);
    if (canonicalKey) {
      normalized[canonicalKey] = value;
    }
  });
  return normalized;
};

const activeLocale = () => i18n.language || DEFAULT_LOCALE;

export const headerLabel = (
  canonicalKey: CsvColumnKey,
  locale: string = activeLocale(),
): string =>
  LABELS_BY_LOCALE[locale]?.[canonicalKey] ??
  LABELS_BY_LOCALE[DEFAULT_LOCALE][canonicalKey];

export const localizedHeaders = (
  canonicalKeys: readonly CsvColumnKey[],
  locale: string = activeLocale(),
): string[] => canonicalKeys.map((key) => headerLabel(key, locale));

export const englishHeaders = (
  canonicalKeys: readonly CsvColumnKey[],
): string[] => canonicalKeys.map((key) => ENGLISH_LABELS[key]);

// Unknown keys pass through, so the error report's appended columns survive.
export const localizeLabelKeys = <T>(
  row: Record<string, T>,
  locale: string = activeLocale(),
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(row).map(([label, value]) => {
      const canonicalKey = normalizeHeader(label);
      return [canonicalKey ? headerLabel(canonicalKey, locale) : label, value];
    }),
  );
